import type { AxiosError } from 'axios';
import axiosInstance from '../lib/axios';
import type { ApiResponse } from './api';

export type CouponOtherStatus =
  | 'not_applied_lower'
  | 'COUPON_INVALID'
  | 'COUPON_NOT_STARTED'
  | 'COUPON_EXPIRED'
  | 'COUPON_NOT_APPLICABLE'
  | 'COUPON_MIN_ORDER'
  | 'COUPON_NEW_PAYERS_ONLY'
  | 'COUPON_USER_LIMIT';

export interface PaymentQuote {
  listAmountMinor: number;
  discountMinor: number;
  amountMinor: number;
  currency: string;
  best: { code: string } | null;
  others?: { code: string; status: CouponOtherStatus }[];
  suggested?: string[];
}

export interface SepayCheckoutForm {
  url: string;
  fields: { name: string; value: string }[];
}

export type PaymentStatus =
  | 'PENDING'
  | 'SUCCESS'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELED'
  | 'NEEDS_REVIEW'
  | 'REFUNDED';

export interface PaymentOrder {
  publicId: string;
  status: PaymentStatus;
  kind: string;
  amountMinor: number;
  listAmountMinor: number;
  discountMinor: number;
  coupon: { code: string } | null;
  currency: string;
  expiresAt: string | null;
  paidAt: string | null;
  item: { publicId: string; name: string } | null;
}

export interface CheckoutResult extends PaymentOrder {
  requiresPayment: boolean;
  reused: boolean;
  checkout: SepayCheckoutForm | null;
}

export interface CheckoutInput {
  kind: 'token_pack' | 'package';
  itemPublicId: string;
  billingPeriod?: 'month' | 'year' | 'one_time';
  couponCodes?: string[];
  expectedAmountMinor?: number;
}

type ErrorBody = { message?: string; error?: string; errorCode?: string; data?: unknown };

function failure<T>(error: unknown, fallback: string): ApiResponse<T> {
  const body = (error as AxiosError<ErrorBody>).response?.data;
  return {
    success: false,
    error: body?.error || body?.message || fallback,
    errorCode: body?.errorCode || body?.error,
    message: body?.message,
    data: body?.data as T | undefined,
  };
}

async function call<T>(
  request: () => Promise<{ data: { data?: T } & Record<string, unknown> }>,
  fallback: string
): Promise<ApiResponse<T>> {
  try {
    const response = await request();
    return { success: true, data: (response.data.data ?? response.data) as T };
  } catch (error) {
    return failure<T>(error, fallback);
  }
}

export const previewCoupons = (input: {
  kind: 'token_pack' | 'package';
  itemPublicId: string;
  billingPeriod?: 'month' | 'year' | 'one_time';
  codes: string[];
}) =>
  call<PaymentQuote>(
    () => axiosInstance.post('/v1/payments/coupons/preview', input),
    'Failed to check coupon'
  );

export const createCheckout = (input: CheckoutInput) =>
  call<CheckoutResult>(
    () => axiosInstance.post('/v1/payments/checkout', input),
    'Failed to start payment'
  );

export const getPayment = (publicId: string) =>
  call<PaymentOrder>(
    () => axiosInstance.get(`/v1/payments/${encodeURIComponent(publicId)}`),
    'Failed to load payment'
  );

export const cancelPayment = (publicId: string) =>
  call<PaymentOrder>(
    () => axiosInstance.post(`/v1/payments/${encodeURIComponent(publicId)}/cancel`),
    'Failed to cancel payment'
  );

export const confirmFreePayment = (publicId: string) =>
  call<PaymentOrder>(
    () => axiosInstance.post(`/v1/payments/${encodeURIComponent(publicId)}/confirm-free`),
    'Failed to activate'
  );

export interface MyCoupon {
  code: string;
  expiresAt: string;
  discount: {
    type: 'PERCENT' | 'FIXED';
    percentBps: number | null;
    fixedAmountMinor: number | null;
  };
  /** null when no item was given to evaluate against. */
  applicable: boolean | null;
  reason: string | null;
  discountMinor: number | null;
}

export const getMyCoupons = (params: {
  kind?: 'token_pack' | 'package';
  itemPublicId?: string;
  billingPeriod?: 'month' | 'year' | 'one_time';
}) =>
  call<{ items: MyCoupon[] }>(
    () => axiosInstance.get('/v1/payments/coupons/mine', { params }),
    'Failed to load coupons'
  );
